import * as React from 'react'
import { useNavigate } from 'react-router-dom'
import { solid } from '@fortawesome/fontawesome-svg-core/import.macro'

import { isFreeTrialPromoCode } from '@/selectors/user.selectors'
import { useFetchResponse, useReselector } from '@/utils/sharedHooks'
import { getMissingStatements } from '../lib/userDocuments.slice'
import { MissingStatementsAccordion } from '@/components/documents/MissingStatementsAccordion'
import { mtc, TypographyStyles } from '@/styles'
import { missingStatementsStyles } from './styles'
import {
  Button,
  Card,
  CardColors,
  Heading,
  HeadingVariants,
  Icon,
  Link,
  Text,
} from '@/ui'

interface MissingStatementsProps {
  includeUploadButton?: boolean
  accordionOnly?: boolean
  whenNone?: React.ReactNode
}

export const MissingStatements = ({
  includeUploadButton = true,
  accordionOnly = false,
  whenNone = null,
}: MissingStatementsProps) => {
  const navigate = useNavigate()
  const missingStatements = useFetchResponse(getMissingStatements)
  const hasFreeTrialPromoCode = useReselector(isFreeTrialPromoCode)

  if (
    !missingStatements ||
    missingStatements.length === 0 ||
    missingStatements.every((account) => account.statements.length === 0)
  ) {
    return whenNone
  }

  if (hasFreeTrialPromoCode) {
    return null
  }

  if (accordionOnly) {
    return (
      <MissingStatementsAccordion
        neededStatements={missingStatements}
        title="View missing statements"
      />
    )
  }

  return (
    <Card color={CardColors.LEVEL_0} className={mtc(missingStatementsStyles())}>
      <div className="flex flex-row items-center gap-2">
        <Icon
          icon={solid('triangle-exclamation')}
          color="pb-1 text-orange-100"
        />
        <Heading variant={HeadingVariants.H2}>Bank statement(s) needed</Heading>
      </div>

      <Text variant={TypographyStyles.BODY_MD} className="mt-2">
        Please upload the bank statements below so we can complete your
        bookkeeping.{' '}
        <Link
          href="https://support.joinheard.com/hc/en-us/articles/23569956149399-Bank-Statements-at-Heard"
          newPage
        >
          Why?
        </Link>
      </Text>

      <br />
      <MissingStatementsAccordion
        neededStatements={missingStatements}
        title="View missing statements"
      />

      {includeUploadButton && (
        <Button
          onClick={() => navigate('/practice/documents')}
          className="mt-6 w-fit self-end"
        >
          Upload in Documents
        </Button>
      )}
    </Card>
  )
}
